/**
 * SendToPlaysHubModal — admin-only flow to ship a play from the editor to
 * the public Plays Hub. Captures the first frame's DOM via html2canvas,
 * crops it to the top 60-70% (so the "second half" of the play remains
 * behind the paywall), uploads the PNG to backend object storage, and
 * creates a queued/published Plays Hub entry.
 */
import React from 'react';
import html2canvas from 'html2canvas';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Wand2, X as XIcon, Upload, Loader2 } from 'lucide-react';

const CATS = ['set', 'man', 'zone', 'ato', 'blob', 'slob', 'endgame', 'press_break', 'transition'];
const LANGS = ['es', 'en', 'fr'];
const CROP_PCT = 0.65; // Keep top 65% of the canvas — hides the second half.

async function captureCroppedSnapshot(node) {
  // Render the play frame to a canvas at decent resolution.
  const full = await html2canvas(node, {
    backgroundColor: '#0A192F',
    scale: 1.5,
    useCORS: true,
    logging: false,
  });
  // Crop to the top CROP_PCT.
  const cropped = document.createElement('canvas');
  cropped.width = full.width;
  cropped.height = Math.round(full.height * CROP_PCT);
  const ctx = cropped.getContext('2d');
  ctx.drawImage(full, 0, 0, full.width, cropped.height, 0, 0, cropped.width, cropped.height);
  // Add a subtle "Vista parcial" watermark + gradient at the bottom to make
  // the crop look intentional, not truncated.
  const grad = ctx.createLinearGradient(0, cropped.height * 0.75, 0, cropped.height);
  grad.addColorStop(0, 'rgba(10,25,47,0)');
  grad.addColorStop(1, 'rgba(10,25,47,0.95)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, cropped.height * 0.75, cropped.width, cropped.height * 0.25);
  return new Promise((resolve) => cropped.toBlob((b) => resolve({ blob: b, dataUrl: cropped.toDataURL('image/png') }), 'image/png', 0.92));
}

export default function SendToPlaysHubModal({
  play,            // { id, name, category, description }
  exportNode,      // DOM node of the play's first frame (export ref)
  onClose,
  onSent,
}) {
  const [preview, setPreview] = React.useState(null);
  const [blob, setBlob] = React.useState(null);
  const [busy, setBusy] = React.useState('snap');  // snap | idle | uploading | done
  const [title, setTitle] = React.useState(play?.name || '');
  const [summary, setSummary] = React.useState((play?.description || '').slice(0, 220));
  const [summaryFull, setSummaryFull] = React.useState(play?.description || '');
  const [category, setCategory] = React.useState(CATS.includes(play?.category) ? play.category : 'set');
  const [language, setLanguage] = React.useState('es');
  const [tags, setTags] = React.useState('');
  const [publishNow, setPublishNow] = React.useState(false);

  // Capture the snapshot on mount.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!exportNode) {
        toast.error('No hay frame para capturar.');
        onClose();
        return;
      }
      try {
        const { blob: b, dataUrl } = await captureCroppedSnapshot(exportNode);
        if (cancelled) return;
        setBlob(b);
        setPreview(dataUrl);
        setBusy('idle');
      } catch (e) {
        console.error(e);
        toast.error('No pudimos capturar el snapshot.');
        onClose();
      }
    })();
    return () => { cancelled = true; };
  }, [exportNode, onClose]);

  const submit = async () => {
    if (!blob) return;
    if (title.trim().length < 3) { toast.error('Título ≥ 3 caracteres'); return; }
    if (summary.trim().length < 10) { toast.error('Resumen ≥ 10 caracteres'); return; }
    setBusy('uploading');
    try {
      // 1. Upload the image
      const form = new FormData();
      form.append('image', blob, 'playshub.png');
      const upload = await api.post('/playshub/admin/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = upload.data?.url;
      if (!imageUrl) throw new Error('Upload returned no URL');

      // 2. Create the Plays Hub entry (queued by default).
      const created = await api.post('/playshub/admin/plays', {
        title: title.trim(),
        summary_partial: summary.trim(),
        summary_full: summaryFull.trim() || null,
        category,
        language,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
        image_url: imageUrl,
        image_partial_url: imageUrl,
        source: 'playbook',
        source_play_id: play?.id,
      });

      // 3. If "publish now" is checked, flip status immediately.
      if (publishNow && created.data?.id) {
        await api.patch(`/playshub/admin/plays/${created.data.id}/status?status=published`);
      }

      toast.success(publishNow ? '¡Jugada publicada en el hub!' : 'Jugada agregada a la cola.');
      onSent?.(created.data);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'No se pudo enviar al Plays Hub.');
      setBusy('idle');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="send-playshub-modal">
      <div className="w-full max-w-3xl rounded-xl border border-gold/40 bg-navy-deep p-6 max-h-[92vh] overflow-y-auto">
        <header className="flex items-center justify-between mb-4">
          <h3 className="font-h text-xl uppercase tracking-tight flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-gold" /> Enviar al Plays Hub
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Cerrar">
            <XIcon className="w-5 h-5" />
          </button>
        </header>

        {/* Preview */}
        <div className="mb-4 rounded-lg overflow-hidden border border-gold/20 bg-navy">
          {busy === 'snap' || !preview ? (
            <div className="aspect-[16/10] grid place-items-center text-slate-400 text-xs font-mono uppercase tracking-widest">
              <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Capturando snapshot…</span>
            </div>
          ) : (
            <img src={preview} alt="Preview" className="w-full block" data-testid="send-playshub-preview" />
          )}
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 gap-3">
          <input
            data-testid="send-playshub-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título SEO"
            className="bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm"
            maxLength={120}
          />
          <textarea
            data-testid="send-playshub-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            placeholder="Resumen parcial (público, ideal 90-220 chars terminando con un teaser)"
            className="bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm"
            maxLength={600}
          />
          <textarea
            value={summaryFull}
            onChange={(e) => setSummaryFull(e.target.value)}
            rows={4}
            placeholder="Resumen completo (solo logueados — pasos, opciones, lecturas)"
            className="bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm"
            maxLength={4000}
          />
          <div className="grid grid-cols-3 gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="tags,separados,por,coma"
              className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="send-playshub-publish-now"
              checked={publishNow}
              onChange={(e) => setPublishNow(e.target.checked)}
              className="rounded border-gold/30 bg-navy"
            />
            <span>Publicar inmediatamente (sin pasar por la cola)</span>
          </label>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-300 hover:text-white">
            Cancelar
          </button>
          <button
            data-testid="send-playshub-submit"
            onClick={submit}
            disabled={busy !== 'idle'}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy-deep text-xs font-mono uppercase tracking-widest disabled:opacity-50 hover:bg-gold/90"
          >
            {busy === 'uploading'
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</>
              : <><Upload className="w-3.5 h-3.5" /> Enviar al hub</>}
          </button>
        </div>
      </div>
    </div>
  );
}
